import type { HandLandmark, HandSample } from '../domain/types'

const FINGER_JOINTS = [
  [5, 6, 8],
  [9, 10, 12],
  [13, 14, 16],
  [17, 18, 20]
] as const

export interface FingerFlags {
  thumb: boolean
  index: boolean
  middle: boolean
  ring: boolean
  pinky: boolean
}

export function extendedFingerFlags(hand: HandSample): FingerFlags {
  const flags: FingerFlags = { thumb: false, index: false, middle: false, ring: false, pinky: false }
  if (hand.landmarks.length < 21) return flags
  const wrist = hand.landmarks[0]!
  const names: (keyof FingerFlags)[] = ['index', 'middle', 'ring', 'pinky']
  FINGER_JOINTS.forEach(([mcpIndex, pipIndex, tipIndex], position) => {
    const mcp = hand.landmarks[mcpIndex]!
    const pip = hand.landmarks[pipIndex]!
    const tip = hand.landmarks[tipIndex]!
    const reachesPastPalm = distance(wrist, tip) > distance(wrist, mcp) * 1.45
    const reachesPastJoint = distance(wrist, tip) > distance(wrist, pip) * 1.22
    flags[names[position]!] = reachesPastPalm && reachesPastJoint
  })
  const thumbIp = hand.landmarks[3]!
  const thumbTip = hand.landmarks[4]!
  const thumbMcp = hand.landmarks[2]!
  flags.thumb =
    distance(wrist, thumbTip) > distance(wrist, thumbIp) * 1.18 &&
    distance(thumbTip, thumbMcp) > distance(thumbIp, thumbMcp) * 1.35
  return flags
}

export function countExtendedFingers(hand: HandSample): number {
  const flags = extendedFingerFlags(hand)
  return [flags.thumb, flags.index, flags.middle, flags.ring, flags.pinky].filter(Boolean).length
}

export function fingerCountConfidence(hand: HandSample): number {
  if (hand.landmarks.length < 21) return 0
  const palmWidth = distance(hand.landmarks[5]!, hand.landmarks[17]!)
  const palmHeight = distance(hand.landmarks[0]!, hand.landmarks[9]!)
  const facingScore = clamp01(palmWidth / Math.max(0.001, palmHeight) / 0.8)
  return clamp01(hand.confidence) * (0.35 + facingScore * 0.65)
}

export function pinchStrength(hand: HandSample): number {
  if (hand.landmarks.length < 21) return 0
  const palmWidth = Math.max(0.001, distance(hand.landmarks[5]!, hand.landmarks[17]!))
  return clamp01(1 - distance(hand.landmarks[4]!, hand.landmarks[8]!) / (palmWidth * 0.72))
}

export function palmCentroid(hand: HandSample): HandLandmark {
  const points = [0, 5, 9, 13, 17].map((index) => hand.landmarks[index]!).filter(Boolean)
  if (points.length === 0) return { x: 0.5, y: 0.5, z: 0 }
  return points.reduce((sum, point) => ({ x: sum.x + point.x / points.length, y: sum.y + point.y / points.length, z: sum.z + point.z / points.length }), { x: 0, y: 0, z: 0 })
}

function distance(a: HandLandmark, b: HandLandmark): number {
  return Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z)
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value))
}
